import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { avisar } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import {
  ErroDaApi,
  apiMarketing,
  type AchadoNoPost,
  type ConteudoDoPost as Conteudo,
  type LimiteDaRede,
  type PostDeMarketing,
  type TextoDoSlide,
  type TextosDoPost,
} from '../lib/api.ts';
import { NOME_DA_REGRA, caracteres, copiar, hashtagsDe, legendaComHashtags } from '../lib/posts.ts';

/**
 * ⭐⭐ O CONTEÚDO DO POST — a parte 2 da aba Marketing, 15/10/2026 (PRD §50.5).
 *
 * Os slides em ordem, a legenda e as hashtags do Instagram e o texto do
 * LinkedIn, com a conferência ao lado de cada campo e os botões de copiar
 * (hoje a publicação é à mão).
 *
 * - ⭐ **A conferência roda enquanto se escreve**, no servidor, sobre o
 *   rascunho (`POST /conferencia`, sem gravar): o achado aparece junto do
 *   campo antes de salvar. ⚠️ **Avisa, não bloqueia.**
 * - ⭐ **Os contadores andam a cada letra**, com os limites que vêm do servidor
 *   (`conteudo.limites`): a tela não guarda uma segunda cópia dos números.
 * - ⛔ **O fecho institucional não se digita**: é o último slide do carrossel,
 *   fixo, e entra sozinho na montagem (parte 4).
 * - ⛔ **"Usar a legenda do Instagram" só preenche o vazio**: a regra da casa
 *   de nenhum botão apagar o que existe (CLAUDE.md, 15/09/2026).
 * - ⚠️ **Gravar pergunta antes**, como todo formulário da casa, e a gravação
 *   leva a versão: outra janela que gravou antes faz esta receber 409, e nada
 *   se perde por cima.
 * - ⭐ **Parte 4 (PRD §50.7): o desenho do manifesto** é uma opção do slide da
 *   imagem única, marcada pela pessoa (o Hub não adivinha). E o texto que não
 *   cabe na arte aparece junto do campo, como os outros achados.
 *
 * ⛔⛔ **Fala só com `/api/marketing`** (PRD §7.1).
 */

type SlideNaTela = TextoDoSlide & { chave: string };
type Rascunho = { slides: SlideNaTela[]; legenda: string; hashtags: string; textoLinkedin: string };

let proxima = 1;
/** A chave do slide que ainda não tem id: o React precisa dela para não trocar um cartão pelo outro ao reordenar. */
const novaChave = () => `novo-${proxima++}`;

const slideVazio = (): SlideNaTela => ({ chave: novaChave(), rotulo: '', titulo: '', apoio: '', textoAlternativo: '', manifesto: false });

function doGravado(c: Conteudo): Rascunho {
  return {
    slides: c.slides.map((s) => ({ chave: s.id, id: s.id, rotulo: s.rotulo, titulo: s.titulo, apoio: s.apoio, textoAlternativo: s.textoAlternativo, manifesto: s.manifesto })),
    legenda: c.legenda,
    hashtags: c.hashtags,
    textoLinkedin: c.textoLinkedin,
  };
}

/** O que vai ao servidor: sem a chave da tela. */
function paraEnviar(r: Rascunho): TextosDoPost {
  return {
    slides: r.slides.map(({ chave: _chave, ...s }) => s),
    legenda: r.legenda,
    hashtags: r.hashtags,
    textoLinkedin: r.textoLinkedin,
  };
}

const temTexto = (s: TextoDoSlide) => Boolean(s.rotulo.trim() || s.titulo.trim() || s.apoio.trim() || s.textoAlternativo.trim());

/** O endereço de cada campo na página: o achado leva até ele. */
const idDoCampo = (campo: string) => `mkt-c-${campo.replace(/\./g, '-')}`;

const numero = (n: number) => n.toLocaleString('pt-BR');

/** `120 de 2.200`, no tom de quem está perto ou passou. */
function Contador({ valor, limite, unidade }: { valor: number; limite: LimiteDaRede; unidade: string }) {
  const tom = valor > limite.maximo ? ' passou' : valor >= limite.maximo * 0.9 ? ' perto' : '';
  return (
    <span className={`mkt-contador${tom}`} title={limite.nota}>
      <span className="dado">{numero(valor)}</span> de <span className="dado">{numero(limite.maximo)}</span> {unidade}
    </span>
  );
}

/** Os achados de um campo, logo abaixo dele. */
function AchadosDoCampo({ lista }: { lista: AchadoNoPost[] | undefined }) {
  if (!lista?.length) return null;
  return (
    <ul className="mkt-achados-do-campo">
      {lista.map((a, i) => (
        <li key={i} className={`mkt-achado ${a.gravidade}`}>
          <Icone nome="atencao" tamanho={13} />
          <span>
            <b>{a.gravidade === 'grave' ? 'Grave' : 'Atenção'} · {NOME_DA_REGRA[a.regra] ?? a.regra}.</b> {a.aviso}
            {a.trecho && <q>{a.trecho}</q>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Um campo do conteúdo: o rótulo, o contador ou o botão do lado, o campo, a nota e os achados dele. */
function Campo({
  campo,
  rotulo,
  lado,
  nota,
  achados,
  children,
}: {
  campo: string;
  rotulo: string;
  lado?: ReactNode;
  nota?: string;
  achados: Map<string, AchadoNoPost[]>;
  children: ReactNode;
}) {
  return (
    <div className="imv-campo c6 mkt-campo">
      <div className="mkt-campo-topo">
        <label className="imv-rotulo" htmlFor={idDoCampo(campo)}>
          {rotulo}
        </label>
        {lado}
      </div>
      <span className={`imv-in${achados.get(campo)?.some((a) => a.gravidade === 'grave') ? ' mkt-in-grave' : ''}`}>{children}</span>
      {nota && <small>{nota}</small>}
      <AchadosDoCampo lista={achados.get(campo)} />
    </div>
  );
}

export function ConteudoDoPost({
  post,
  conteudo,
  aoGravado,
  aoPendente,
  aoRecarregar,
}: {
  post: PostDeMarketing;
  /** O conteúdo como está GRAVADO. O rascunho nasce dele e vive aqui. */
  conteudo: Conteudo;
  aoGravado: (c: Conteudo) => void;
  /** Diz à ficha se há alteração por gravar (a escada e a saída perguntam). */
  aoPendente: (pendente: boolean) => void;
  /** Depois do 409: a ficha relê o gravado e remonta este bloco. */
  aoRecarregar: () => void;
}) {
  const [r, setR] = useState<Rascunho>(() => doGravado(conteudo));
  /** O rascunho como está gravado: é para onde "Descartar" volta. */
  const [base, setBase] = useState<Rascunho>(() => doGravado(conteudo));
  const gravado = useMemo(() => JSON.stringify(paraEnviar(base)), [base]);
  const [versao, setVersao] = useState(conteudo.versao);
  const [achados, setAchados] = useState<AchadoNoPost[]>(conteudo.achados);
  const [conferindo, setConferindo] = useState(false);
  const [conferenciaFalhou, setConferenciaFalhou] = useState(false);
  const [pedindo, setPedindo] = useState<'salvar' | null>(null);
  const [descartando, setDescartando] = useState(false);
  const [aRemover, setARemover] = useState<number | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [outraJanela, setOutraJanela] = useState(false);
  const [focar, setFocar] = useState<string | null>(null);

  const enviavel = useMemo(() => paraEnviar(r), [r]);
  const json = JSON.stringify(enviavel);
  const sujo = json !== gravado;
  const plano = `${post.formato}|${post.redes.join(',')}`;
  /** Para que texto e que plano os achados na tela valem. */
  const conferidoPara = useRef(`${json}|${plano}`);
  const seq = useRef(0);

  useEffect(() => {
    aoPendente(sujo);
  }, [sujo, aoPendente]);

  /* Fechar a aba com texto por gravar pergunta (o navegador escreve a pergunta, não a casa). */
  useEffect(() => {
    if (!sujo) return;
    const segurar = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', segurar);
    return () => window.removeEventListener('beforeunload', segurar);
  }, [sujo]);

  /*
   * ⭐ **A conferência do rascunho, enquanto se escreve.** Espera a pessoa
   * parar de digitar e pede ao servidor. ⚠️ **A resposta velha não pisa na
   * nova**: só vale a do último pedido. ⚠️ **A falha aparece**: conferência
   * que não respondeu diz isso, em vez de mostrar os achados de antes como se
   * fossem de agora.
   */
  useEffect(() => {
    const chave = `${json}|${plano}`;
    if (chave === conferidoPara.current) return;
    const n = ++seq.current;
    setConferindo(true);
    const espera = window.setTimeout(() => {
      apiMarketing
        .conferir(post.id, JSON.parse(json) as TextosDoPost)
        .then((res) => {
          if (n !== seq.current) return;
          conferidoPara.current = chave;
          setAchados(res.achados);
          setConferenciaFalhou(false);
        })
        .catch(() => n === seq.current && setConferenciaFalhou(true))
        .finally(() => n === seq.current && setConferindo(false));
    }, 450);
    return () => window.clearTimeout(espera);
  }, [json, plano, post.id]);

  /* O slide recém-acrescentado ganha o foco no título. */
  useEffect(() => {
    if (!focar) return;
    document.getElementById(focar)?.focus();
    setFocar(null);
  }, [focar]);

  const porCampo = useMemo(() => {
    const m = new Map<string, AchadoNoPost[]>();
    for (const a of achados) m.set(a.campo, [...(m.get(a.campo) ?? []), a]);
    return m;
  }, [achados]);
  const graves = achados.filter((a) => a.gravidade === 'grave').length;
  const atencoes = achados.length - graves;

  const muda = (mudanca: Partial<Rascunho>) => {
    setErro(null);
    setR((x) => ({ ...x, ...mudanca }));
  };
  const mudaSlide = (i: number, campo: Exclude<keyof TextoDoSlide, 'manifesto'>, valor: string) =>
    setR((x) => ({ ...x, slides: x.slides.map((s, j) => (j === i ? { ...s, [campo]: valor } : s)) }));
  const mudaManifesto = (i: number, manifesto: boolean) => setR((x) => ({ ...x, slides: x.slides.map((s, j) => (j === i ? { ...s, manifesto } : s)) }));
  const moverSlide = (i: number, passo: -1 | 1) =>
    setR((x) => {
      const s = [...x.slides];
      const [um] = s.splice(i, 1);
      s.splice(i + passo, 0, um!);
      return { ...x, slides: s };
    });
  const tirarSlide = (i: number) => setR((x) => ({ ...x, slides: x.slides.filter((_, j) => j !== i) }));

  function acrescentar() {
    const novo = slideVazio();
    setR((x) => ({ ...x, slides: [...x.slides, novo] }));
    setFocar(idDoCampo(`slides.${r.slides.length}.titulo`));
  }

  async function gravar() {
    setPedindo(null);
    setSalvando(true);
    setErro(null);
    try {
      const novo = await apiMarketing.gravarConteudo(post.id, versao, enviavel);
      const rasc = doGravado(novo);
      conferidoPara.current = `${JSON.stringify(paraEnviar(rasc))}|${plano}`;
      seq.current += 1;
      setR(rasc);
      setBase(rasc);
      setVersao(novo.versao);
      setAchados(novo.achados);
      setConferindo(false);
      aoGravado(novo);
      avisar('Conteúdo gravado.');
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 409) setOutraJanela(true);
      setErro(e instanceof Error ? e.message : 'Não consegui gravar o conteúdo.');
    } finally {
      setSalvando(false);
    }
  }

  const comImagem = post.formato !== 'texto';
  const fecho = conteudo.fecho;
  const podeAcrescentar = comImagem && (post.formato !== 'imagem_unica' || r.slides.length === 0) && r.slides.length < 30;
  const mostraInstagram = post.redes.includes('instagram') || Boolean(r.legenda.trim() || r.hashtags.trim());
  const mostraLinkedin = post.redes.includes('linkedin') || Boolean(r.textoLinkedin.trim());
  const legendaInteira = legendaComHashtags(r.legenda, r.hashtags);
  const totalDeHashtags = hashtagsDe(r.legenda).length + hashtagsDe(r.hashtags).length;
  const lim = conteudo.limites;

  return (
    <section className="s-folha mkt-conteudo" aria-label="Conteúdo do post">
      <header className="s-folha-topo mkt-conteudo-topo">
        <div>
          <h2 className="s-folha-titulo">Conteúdo</h2>
          <p className="mkt-nota">Os slides, a legenda e os textos de cada rede. A conferência roda enquanto se escreve.</p>
        </div>
        <div className="mkt-gravar">
          {sujo ? (
            <>
              <span className="mkt-por-gravar">Há alteração por gravar.</span>
              <button type="button" className="imv-bt fantasma" onClick={() => setDescartando(true)}>
                Descartar
              </button>
            </>
          ) : (
            <span className="mkt-gravado">Tudo gravado.</span>
          )}
          <button type="button" className="imv-bt primario" disabled={!sujo || salvando} onClick={() => setPedindo('salvar')}>
            <Icone nome="check" tamanho={14} /> {salvando ? 'Gravando…' : 'Gravar o conteúdo'}
          </button>
        </div>
      </header>

      {/* ── A conferência, resumida: o detalhe mora junto de cada campo ── */}
      <div className={`mkt-conferencia${graves ? ' grave' : atencoes ? ' atencao' : ' limpa'}`} aria-live="polite">
        <div className="mkt-conferencia-topo">
          <Icone nome={achados.length ? 'atencao' : 'concluido'} tamanho={16} />
          <b>Conferência</b>
          <span>
            {achados.length === 0
              ? 'Nada a apontar no conteúdo.'
              : [graves && `${graves} ${graves === 1 ? 'grave' : 'graves'}`, atencoes && `${atencoes} de atenção`].filter(Boolean).join(' e ') + '.'}
            {graves > 0 && ' Aprovar o post vai perguntar antes.'}
          </span>
          {conferindo && <span className="mkt-conferindo">conferindo…</span>}
        </div>
        {conferenciaFalhou && (
          <p className="mkt-conferencia-falhou">A conferência não respondeu agora. Os achados abaixo são da última vez que respondeu.</p>
        )}
        {achados.length > 0 && (
          <ul className="mkt-onde">
            {[...achados]
              .sort((a, b) => (a.gravidade === b.gravidade ? 0 : a.gravidade === 'grave' ? -1 : 1))
              .map((a, i) => (
                <li key={i}>
                  <button
                    type="button"
                    className={`mkt-onde-item ${a.gravidade}`}
                    onClick={() => {
                      const el = document.getElementById(idDoCampo(a.campo === 'post' ? 'legenda' : a.campo));
                      el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.focus({ preventScroll: true });
                    }}
                  >
                    <i aria-hidden="true" />
                    {a.onde} · {NOME_DA_REGRA[a.regra] ?? a.regra}
                  </button>
                </li>
              ))}
          </ul>
        )}
      </div>

      {erro && (
        <div className="mkt-erro-gravar">
          <p className="erro">{erro}</p>
          {outraJanela && (
            <p>
              Recarregue para ver o que a outra janela gravou. Antes, copie daqui o que quiser manter: ao recarregar, o que está nesta tela sai.{' '}
              <button type="button" className="imv-bt p" onClick={aoRecarregar}>
                Recarregar o conteúdo
              </button>
            </p>
          )}
        </div>
      )}

      <div className="mkt-conteudo-grade">
        {/* ── Os slides ──────────────────────────────────────────────── */}
        <div className="mkt-coluna">
          <div className="mkt-bloco-topo" id={idDoCampo('slides')} tabIndex={-1}>
            <h3 className="mkt-bloco-titulo">Slides</h3>
            <span className="mkt-nota">
              {!comImagem
                ? 'Post só de texto: não leva imagem.'
                : post.formato === 'imagem_unica'
                  ? 'Imagem única: um slide.'
                  : `${r.slides.length} ${r.slides.length === 1 ? 'slide' : 'slides'} e o fecho.`}
            </span>
            {podeAcrescentar && (
              <button type="button" className="imv-bt p" onClick={acrescentar}>
                <Icone nome="mais" tamanho={13} /> Acrescentar slide
              </button>
            )}
          </div>
          <AchadosDoCampo lista={porCampo.get('slides')} />

          {r.slides.length === 0 && comImagem && <p className="mkt-vazio-bloco">Nenhum slide ainda.</p>}

          <ol className="mkt-slides">
            {r.slides.map((s, i) => (
              <li key={s.chave} className="mkt-slide">
                <div className="mkt-slide-topo">
                  <span className="mkt-slide-num dado">{i + 1}</span>
                  <b>{i === 0 && r.slides.length > 1 ? 'Capa' : `Slide ${i + 1}`}</b>
                  <span className="mkt-esp" />
                  <button type="button" className="imv-bt fantasma p" disabled={i === 0} onClick={() => moverSlide(i, -1)} aria-label={`Subir o slide ${i + 1}`} title="Subir">
                    <Icone nome="acima" tamanho={14} />
                  </button>
                  <button
                    type="button"
                    className="imv-bt fantasma p"
                    disabled={i === r.slides.length - 1}
                    onClick={() => moverSlide(i, 1)}
                    aria-label={`Descer o slide ${i + 1}`}
                    title="Descer"
                  >
                    <Icone nome="abaixo" tamanho={14} />
                  </button>
                  <button
                    type="button"
                    className="imv-bt fantasma p perigo"
                    onClick={() => (temTexto(s) ? setARemover(i) : tirarSlide(i))}
                    aria-label={`Remover o slide ${i + 1}`}
                  >
                    <Icone nome="lixeira" tamanho={13} /> Remover
                  </button>
                </div>
                {/* ⭐ Parte 4: o desenho do manifesto, só na imagem única, e só no slide que é a imagem. */}
                {post.formato === 'imagem_unica' && i === 0 && (
                  <label className="mkt-marca">
                    <input type="checkbox" checked={Boolean(s.manifesto)} onChange={(e) => mudaManifesto(i, e.target.checked)} />
                    <span>
                      <strong>Desenho do manifesto.</strong> O texto sai em Cormorant Garamond, com evidências, inteligência e recuperar em ouro, como manda o manual.
                      Cada frase vira um parágrafo, e embaixo vão o logotipo e os contatos.
                    </span>
                  </label>
                )}
                <div className="imv-grade-campos">
                  <Campo campo={`slides.${i}.rotulo`} rotulo="Rótulo" nota='O rótulo pequeno do alto, como "Nível 1 · Insight".' achados={porCampo}>
                    <input id={idDoCampo(`slides.${i}.rotulo`)} value={s.rotulo} maxLength={200} onChange={(e) => mudaSlide(i, 'rotulo', e.target.value)} />
                  </Campo>
                  <Campo campo={`slides.${i}.titulo`} rotulo="Título" nota="A linha que você quebrar aqui (Enter) também quebra na arte." achados={porCampo}>
                    <textarea id={idDoCampo(`slides.${i}.titulo`)} rows={2} value={s.titulo} maxLength={1000} onChange={(e) => mudaSlide(i, 'titulo', e.target.value)} />
                  </Campo>
                  <Campo campo={`slides.${i}.apoio`} rotulo="Texto de apoio" achados={porCampo}>
                    <textarea id={idDoCampo(`slides.${i}.apoio`)} rows={3} value={s.apoio} maxLength={3000} onChange={(e) => mudaSlide(i, 'apoio', e.target.value)} />
                  </Campo>
                  <Campo
                    campo={`slides.${i}.textoAlternativo`}
                    rotulo="Texto alternativo"
                    nota="O que o leitor de tela lê no lugar da imagem: o fundo e o texto do slide."
                    achados={porCampo}
                    lado={
                      <span className="mkt-campo-lado">
                        <Contador valor={caracteres(s.textoAlternativo)} limite={lim.textoAlternativo} unidade="caracteres" />
                        <button
                          type="button"
                          className="imv-bt p"
                          disabled={!s.textoAlternativo.trim()}
                          onClick={() => copiar(s.textoAlternativo.trim(), avisar, `Texto alternativo do slide ${i + 1} copiado.`)}
                        >
                          <Icone nome="copiar" tamanho={12} /> Copiar
                        </button>
                      </span>
                    }
                  >
                    <textarea
                      id={idDoCampo(`slides.${i}.textoAlternativo`)}
                      rows={3}
                      value={s.textoAlternativo}
                      maxLength={3000}
                      onChange={(e) => mudaSlide(i, 'textoAlternativo', e.target.value)}
                    />
                  </Campo>
                </div>
              </li>
            ))}

            {/* ⛔ O fecho: fixo, sempre igual, e posto pela montagem. Não se digita. */}
            {fecho && (
              <li className="mkt-slide mkt-fecho">
                <div className="mkt-slide-topo">
                  <span className="mkt-slide-num dado">{r.slides.length + 1}</span>
                  <b>Fecho institucional</b>
                  <span className="mkt-fixo">
                    <Icone nome="cadeado" tamanho={12} /> fixo
                  </span>
                </div>
                <p className="mkt-fecho-nota">Entra sozinho na montagem, sempre igual em todo carrossel. Não se digita.</p>
                <div className="mkt-fecho-amostra" aria-hidden="true">
                  <img src="/marca/kairos-branco.png" alt="" />
                  <span className="mkt-fecho-frase">{fecho.frase}</span>
                  <span className="mkt-fecho-contatos">
                    {fecho.contatos.map((c) => (
                      <span key={c.tipo}>
                        {c.texto}
                        {c.tipo === 'whatsapp' && <small> WhatsApp</small>}
                      </span>
                    ))}
                  </span>
                </div>
                <div className="mkt-fecho-alt">
                  <div className="mkt-campo-topo">
                    <span className="imv-rotulo">Texto alternativo</span>
                    <button type="button" className="imv-bt p" onClick={() => copiar(fecho.textoAlternativo, avisar, 'Texto alternativo do fecho copiado.')}>
                      <Icone nome="copiar" tamanho={12} /> Copiar
                    </button>
                  </div>
                  <p>{fecho.textoAlternativo}</p>
                </div>
              </li>
            )}
          </ol>
        </div>

        {/* ── Os textos de cada rede ─────────────────────────────────── */}
        <div className="mkt-coluna mkt-coluna-redes">
          {mostraInstagram && (
            <div className="mkt-rede-bloco">
              <div className="mkt-bloco-topo">
                <h3 className="mkt-bloco-titulo">
                  <Icone nome="instagram" tamanho={16} /> Instagram
                </h3>
                <button type="button" className="imv-bt p" disabled={!legendaInteira} onClick={() => copiar(legendaInteira, avisar, 'Legenda e hashtags copiadas.')}>
                  <Icone nome="copiar" tamanho={12} /> Copiar legenda e hashtags
                </button>
              </div>
              {!post.redes.includes('instagram') && <p className="mkt-aviso">Este post não está marcado para o Instagram.</p>}
              <AchadosDoCampo lista={porCampo.get('post')} />
              <div className="imv-grade-campos">
                <Campo
                  campo="legenda"
                  rotulo="Legenda"
                  lado={<Contador valor={caracteres(legendaInteira)} limite={lim.legenda} unidade="caracteres" />}
                  nota={lim.legenda.nota}
                  achados={porCampo}
                >
                  <textarea id={idDoCampo('legenda')} rows={12} value={r.legenda} maxLength={10000} onChange={(e) => muda({ legenda: e.target.value })} />
                </Campo>
                <Campo
                  campo="hashtags"
                  rotulo="Hashtags"
                  lado={<Contador valor={totalDeHashtags} limite={lim.hashtags} unidade="hashtags" />}
                  nota="Separadas da legenda aqui, e juntas na cópia, depois de uma linha em branco."
                  achados={porCampo}
                >
                  <input id={idDoCampo('hashtags')} value={r.hashtags} maxLength={2000} placeholder="#recuperaçãodecrédito #execução" onChange={(e) => muda({ hashtags: e.target.value })} />
                </Campo>
              </div>
              <p className="mkt-limite-nota">{lim.hashtags.nota}</p>
            </div>
          )}

          {mostraLinkedin && (
            <div className="mkt-rede-bloco">
              <div className="mkt-bloco-topo">
                <h3 className="mkt-bloco-titulo">
                  <Icone nome="linkedin" tamanho={16} /> LinkedIn
                </h3>
                <button type="button" className="imv-bt p" disabled={!r.textoLinkedin.trim()} onClick={() => copiar(r.textoLinkedin.trim(), avisar, 'Texto do LinkedIn copiado.')}>
                  <Icone nome="copiar" tamanho={12} /> Copiar texto
                </button>
              </div>
              {!post.redes.includes('linkedin') && <p className="mkt-aviso">Este post não está marcado para o LinkedIn.</p>}
              <div className="imv-grade-campos">
                <Campo
                  campo="textoLinkedin"
                  rotulo="Texto do LinkedIn"
                  lado={<Contador valor={caracteres(r.textoLinkedin.trim())} limite={lim.linkedin} unidade="caracteres" />}
                  nota={lim.linkedin.nota}
                  achados={porCampo}
                >
                  <textarea id={idDoCampo('textoLinkedin')} rows={12} value={r.textoLinkedin} maxLength={10000} onChange={(e) => muda({ textoLinkedin: e.target.value })} />
                </Campo>
              </div>
              <div className="mkt-usar-legenda">
                <button
                  type="button"
                  className="imv-bt p"
                  disabled={Boolean(r.textoLinkedin.trim()) || !legendaInteira}
                  title={
                    r.textoLinkedin.trim()
                      ? 'O texto do LinkedIn já tem conteúdo: o botão só preenche o campo vazio.'
                      : 'Traz a legenda e as hashtags do Instagram para cá, para adaptar.'
                  }
                  onClick={() => {
                    /* ⛔ Só no vazio: o que está escrito aqui não se troca por botão. */
                    if (r.textoLinkedin.trim() || !legendaInteira) return;
                    muda({ textoLinkedin: legendaInteira });
                  }}
                >
                  <Icone nome="trazer" tamanho={12} /> Usar a legenda do Instagram
                </button>
                <small>Só preenche o campo vazio. Adapte depois: "o link da bio" não existe no LinkedIn.</small>
              </div>
            </div>
          )}
        </div>
      </div>

      <GuardaDoFormulario
        pendencia={pedindo}
        oQue="o conteúdo do post"
        aviso={graves ? `A conferência aponta ${graves === 1 ? 'um achado grave' : `${graves} achados graves`}: grava assim mesmo, e ${graves === 1 ? 'ele continua apontado' : 'eles continuam apontados'}.` : undefined}
        aoCancelar={() => setPedindo(null)}
        aoSalvar={() => void gravar()}
        aoDescartar={() => setPedindo(null)}
      />

      {descartando && (
        <Confirmar
          titulo="Descartar alterações"
          descricao="O conteúdo volta a ser o que está gravado, e o que foi digitado desde a última gravação sai."
          rotuloAcao="Descartar"
          aoFechar={() => setDescartando(false)}
          aoConfirmar={() => {
            setR(base);
            setErro(null);
          }}
        />
      )}

      {aRemover !== null && (
        <Confirmar
          titulo={`Remover o slide ${aRemover + 1}`}
          descricao="O texto dele sai deste rascunho agora, e do Hub ao gravar, com a imagem do fundo dele, se houver. Até gravar, descartar as alterações o traz de volta."
          rotuloAcao="Remover"
          aoFechar={() => setARemover(null)}
          aoConfirmar={() => {
            tirarSlide(aRemover);
            setARemover(null);
          }}
        />
      )}
    </section>
  );
}
